import { motion } from "framer-motion";
import { ExternalLink, Star } from "lucide-react";
import * as Icons from "lucide-react";
import { Service, Testimonial, PortfolioItem, cn } from "@/lib/index";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Dynamic icon helper to map string names to Lucide icons
const IconRenderer = ({ iconName, className }: { iconName: string; className?: string }) => {
  const IconComponent = (Icons as any)[iconName] || Icons.Zap;
  return <IconComponent className={className} />;
};

/**
 * ServiceCard: Features glassmorphism, hover glow, and smooth scaling
 */
export function ServiceCard({ service }: { service: Service }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02, y: -5 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="group relative"
    >
      {/* Glow Effect Layer */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-accent rounded-xl blur opacity-0 group-hover:opacity-30 transition duration-500" />
      
      <Card className="relative h-full bg-card/40 backdrop-blur-xl border-white/10 overflow-hidden group-hover:border-primary/50 transition-colors">
        <CardHeader className="pb-2">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
            <IconRenderer iconName={service.icon} className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
          </div>
          <CardTitle className="text-xl font-bold tracking-tight">{service.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-muted-foreground leading-relaxed">
            {service.description}
          </CardDescription>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/**
 * TestimonialCard: Glass style with star rating animation
 */
export function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="h-full"
    >
      <Card className="h-full bg-white/5 backdrop-blur-lg border-white/5 flex flex-col justify-between">
        <CardContent className="pt-8">
          <div className="flex gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
              >
                <Star 
                  className={cn(
                    "w-4 h-4", 
                    i < testimonial.rating ? "fill-primary text-primary" : "text-muted-foreground/30"
                  )} 
                />
              </motion.div>
            ))}
          </div>
          <p className="text-foreground/90 italic mb-6 leading-relaxed">
            "{testimonial.content}"
          </p>
        </CardContent>
        <CardHeader className="flex flex-row items-center gap-4 pt-0 border-t border-white/5 bg-white/5">
          <div className="w-10 h-10 rounded-full overflow-hidden border border-white/20">
            <img 
              src={testimonial.avatar} 
              alt={testimonial.name} 
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all"
            />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-sm tracking-wide">{testimonial.name}</span>
            <span className="text-xs text-muted-foreground">{testimonial.role}</span>
          </div>
        </CardHeader>
      </Card>
    </motion.div>
  );
}

/**
 * PortfolioCard: Modern grid layout with live preview thumbnails
 */
export function PortfolioCard({ item }: { item: PortfolioItem }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group relative aspect-[4/3] rounded-xl overflow-hidden"
    >
      {/* Thumbnail Image */}
      <img 
        src={item.image} 
        alt={item.title} 
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />

      {/* Glass Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-60" />
      
      <div className="absolute inset-0 flex flex-col justify-end p-6 translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
        <div className="flex flex-col gap-2">
          <Badge variant="secondary" className="w-fit bg-primary/20 text-primary-foreground backdrop-blur-md border-none">
            {item.category}
          </Badge>
          <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">{item.title}</h3>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileHover={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4"
          >
            <Button 
              asChild 
              variant="default" 
              className="rounded-full bg-white text-black hover:bg-primary hover:text-white transition-all"
            >
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                View Project <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Hover Rim Light Overlay */}
      <div className="absolute inset-0 border border-white/10 rounded-xl pointer-events-none group-hover:border-primary/50 transition-colors" />
    </motion.div>
  );
}
